import { Link } from "react-router-dom";
import { useMeetingIdForActivity } from "@/hooks/use-meeting-summary";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import SectionCard from "@/components/customers/SectionCard";
import FollowUpFlowDialogs from "@/components/salgsmuligheter/FollowUpFlowDialogs";
import { useCustomerFollowUps, type FollowUpWithRelations } from "@/hooks/use-follow-ups";
import { useFollowUpFlow } from "@/hooks/use-follow-up-flow";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { usePermissions } from "@/hooks/use-permissions";
import { meetingFollowUpLabel, primaryActionFor, PRIORITY_LABELS, priorityTone, sortQueue } from "@/lib/follow-up-calc";
import { FOLLOW_UP_ACTIONS } from "@/lib/follow-up-labels";
import { formatDate } from "@/lib/format";
import { osloDateString } from "@/lib/oslo-time";
import { TONE_TEXT } from "@/lib/widget-status";
import type { FollowUpPriority } from "@/lib/db-types";

/** «Åpne referatet» for et godkjenningskort; vises først når møtet er funnet. */
function OpenSummaryLink({ followUp }: { followUp: FollowUpWithRelations }) {
  const { data: meetingId } = useMeetingIdForActivity(followUp.source_activity_id);
  if (!meetingId) return null;
  return (
    <Button asChild size="sm">
      <Link to={`/kunde/${followUp.customer_id}/moter/${meetingId}`} aria-label={`${meetingFollowUpLabel(followUp)}: ${followUp.title}`}>{meetingFollowUpLabel(followUp)}</Link>
    </Button>
  );
}

const GROUPS: FollowUpPriority[] = ["asap", "this_week", "nice"];

/**
 * «Neste steg» på kundekortet: kundens åpne oppfølginger gruppert på prioritet,
 * med teller («3 forslag · 1 haster»), lenke til ukeslisten og samme utfall som
 * køkortet. «Ny handling» ligger i heroens knapperad. Uten oppfølginger er
 * kortet én linje.
 */
export default function CustomerNextStepsSection({ customerId }: { customerId: string }) {
  const { data: followUps = [], isLoading } = useCustomerFollowUps(customerId);
  const flow = useFollowUpFlow();
  const { activeUser } = useImpersonation();
  const { isAdmin } = usePermissions();
  const today = osloDateString(new Date());
  const sorted = sortQueue<FollowUpWithRelations>(followUps);
  const urgent = sorted.filter((f) => f.priority === "asap" || f.due_date < today).length;

  return (
    <>
      <SectionCard
        title="Neste steg"
        meta={sorted.length > 0 ? `${sorted.length} forslag${urgent > 0 ? ` · ${urgent} haster` : ""}` : undefined}
        empty="Ingen åpne oppfølginger. «Ny handling» øverst lager en, og reglene foreslår selv når noe står stille."
        actions={
          <Link to="/salgsmuligheter" className="text-sm text-primary hover:underline">
            Åpne ukeslisten
          </Link>
        }
      >
        {isLoading ? (
          <Skeleton className="m-5 h-16" />
        ) : sorted.length === 0 ? null : (
          <div className="space-y-4 px-5 py-4">
            {GROUPS.map((priority) => {
              const rows = sorted.filter((f) => f.priority === priority);
              if (rows.length === 0) return null;
              return (
                <div key={priority} className="space-y-1">
                  <h3 className={`text-xs font-medium uppercase tracking-wide ${priorityTone(priority) ? TONE_TEXT[priorityTone(priority)!] : "text-muted-foreground"}`}>
                    {PRIORITY_LABELS[priority]}
                  </h3>
                  <ul className="divide-y divide-border">
                    {rows.map((f) => (
                      <li key={f.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2.5 text-sm">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{f.title}</span>
                          {f.description && (
                            <span className="block text-sm text-muted-foreground line-clamp-2">{f.description}</span>
                          )}
                          <span className={`block text-xs ${f.due_date < today ? TONE_TEXT.miss : "text-muted-foreground"}`}>
                            {formatDate(f.due_date)}
                            {f.due_date < today ? " (forfalt)" : ""}
                            {f.contact ? ` · ${f.contact.full_name}` : ""}
                          </span>
                        </span>
                        {/* close_follow_up og postpone_follow_up krever eier eller admin; andre ser bare hvem som eier den. */}
                        {f.owner_id !== activeUser?.id && !isAdmin ? (
                          <span className="text-xs text-muted-foreground">
                            {f.owner_id ? `Eies av ${f.owner?.full_name ?? "en kollega"}` : "Uten eier"}
                          </span>
                        ) : (
                          <span className="flex flex-wrap gap-1">
                            {meetingFollowUpLabel(f) !== null && <OpenSummaryLink followUp={f} />}
                            {FOLLOW_UP_ACTIONS.filter(({ action }) => meetingFollowUpLabel(f) === null || action === "postpone" || action === "close").map(({ action, label }) => (
                              <Button
                                key={action}
                                size="sm"
                                variant={action === primaryActionFor(f) && meetingFollowUpLabel(f) === null ? "default" : "outline"}
                                onClick={() => flow.start(action, f)}
                                aria-label={`${label}: ${f.title}`}
                              >
                                {label}
                              </Button>
                            ))}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <FollowUpFlowDialogs flow={flow} />
    </>
  );
}
